import { EVENT_DELAY } from '@src/config/constants'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'
import { TelemetryIntegration } from '../telemetry-events.types'

declare global {
  interface Window {
    ga: any
  }
}

export const state = {
  enhancedEcommerceLoaded: false,
}

export const loadEnhancedEcommerce = () => {
  if (!state.enhancedEcommerceLoaded) {
    window.ga('require', 'ec')
    window.ga('set', '&cu', 'USD')
    state.enhancedEcommerceLoaded = true
  } else {
    window.ga('set', '&cu', 'USD')
  }
}

export const GoogleAnalytics: TelemetryIntegration = {
  // Note: Not handling Page view event here, as the Google Analytics gatsby plugin takes care of sending ga page view
  // event. https://github.com/gatsbyjs/gatsby/blob/master/packages/gatsby-plugin-google-analytics/src/gatsby-browser.js

  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.GOOGLE_TAG_MANAGER,
  },

  initialize: () => {
    if (isBrowser()) {
      if (window.ga == undefined) {
        window.ga = function (args: any) {
          if (window.ga) {
            window.ga.q = window.ga.q ?? []
            window.ga.q.push(args)
          }
        }
      }
    }
  },

  events: {
    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: properties => {
      loadEnhancedEcommerce()
      logEnhancedEcommerceProductAction(properties, 'detail')
      logEnhancedEcommerceEvent('Viewed Product')
    },

    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      loadEnhancedEcommerce()
      logEnhancedEcommerceProductAction(properties, 'add')
      logEnhancedEcommerceEvent('Added Product')
    },

    [TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE]: () => {
      logGAEvent('view_product', 'zoom_product_photo')
    },

    [TELEMETRY_EVENT_NAMES.IS_VIDEO_WATCHED]: () => {
      logGAEvent('view_product', 'is_video_watched')
    },

    [TELEMETRY_EVENT_NAMES.REMOVE_FROM_CART]: () => {
      logGAEvent('checkout', 'remove_from_cart')
    },

    [TELEMETRY_EVENT_NAMES.ACCORDION_TOGGLE]: properties => {
      logGAEvent(properties.category, properties.action, properties.label, properties.value)
    },

    [TELEMETRY_EVENT_NAMES.BUTTON_CLICK]: properties => {
      logGAEvent(properties.category, properties.action, properties.label, properties.value)
    },

    [TELEMETRY_EVENT_NAMES.LINK_CLICK]: properties => {
      logGAEvent(properties.category, properties.action, properties.label, properties.value)
    },

    [TELEMETRY_EVENT_NAMES.FORM_SUBMIT]: properties => {
      logGAEvent(properties.category, properties.action, properties.label, properties.value)
    },

    [TELEMETRY_EVENT_NAMES.PAGE_SCROLL]: properties => {
      logGAEvent('Scroll', undefined, undefined, properties.scrollPercentage, true)
    },

    [TELEMETRY_EVENT_NAMES.IDME_VERIFICATION]: properties => {
      logGAEvent(properties.category, properties.action, properties.label, properties.value)
    },

    [TELEMETRY_EVENT_NAMES.SEARCH]: properties => {
      // send a virtual pageview to Google Analytics
      executeFireAndForgotTask(() => {
        if (window.ga) {
          window.ga(
            'send',
            'pageview',
            `/?q=${properties.searchQuery ? properties.searchQuery.toLowerCase() : properties.searchQuery}`,
          )
        }
      }, EVENT_DELAY)
    },

    [TELEMETRY_EVENT_NAMES.EMAIL_NEWSLETTER_SUBSCRIBE]: properties => {
      logGAEvent('leads', 'email_subscription', properties.sourceLabel ?? '')
    },
  },
}

// Pushes normal event to GA with category as enhanced ecommerce.
const logEnhancedEcommerceEvent = (eventAction: unknown) => {
  logGAEvent('EnhancedEcommerce', eventAction, undefined, undefined, true)
}

const logGAEvent = (category: unknown, action: unknown, label?: unknown, value?: unknown, nonInteraction?: unknown) => {
  executeFireAndForgotTask(() => {
    if (window.ga) {
      window.ga('send', 'event', {
        eventCategory: category,
        eventAction: action,
        eventLabel: label,
        eventValue: value,
        nonInteraction: nonInteraction,
      })
    }
  }, EVENT_DELAY)
}

const logEnhancedEcommerceProductAction = (properties: Record<string, unknown>, action: 'detail' | 'add') => {
  const event = {
    id: properties.sku,
    name: (properties.productTitle as string) + ' - ' + (properties.variantTitlem as string),
    category: properties.productType,
    quantity: properties.quantity ? properties.quantity : 1,
    price: properties.price,
    brand: properties.vendor,
    variant: properties.variantTitle,
    currency: 'USD',
    coupon: '',
  }
  // This will not be true for now
  if (properties.coupon) {
    event.coupon = properties.coupon as string
  }
  executeFireAndForgotTask(() => {
    if (window.ga) {
      window.ga('ec:addProduct', event)
      window.ga('ec:setAction', action)
    }
  }, EVENT_DELAY)
}
